import { useEffect, useState } from 'react';

/**
 * Store hours + active blackout dates, for deciding which days can take a
 * delivery.
 *
 * Both already have settings screens (StoreHours, BlackoutDates) and both are
 * already persisted, but the order form was not reading either: it hardcoded
 * "Sundays are closed" plus a HOLIDAYS_2024 list that went stale in 2025. So a
 * shop that opened on Sundays, or blacked out Valentine's week, was telling the
 * settings one thing and the order form another.
 *
 * Fails OPEN. If either request fails, the caller sees no restrictions rather
 * than an empty set of dates — a counter who cannot pick any date is worse off
 * than one offered a day the shop happens to be closed.
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `http://${window.location.hostname}:3001`
    : 'http://localhost:3001');
const API_PREFIX =
  process.env.NODE_ENV === 'production' && !process.env.REACT_APP_API_URL?.includes('/api')
    ? '/api'
    : '';

/**
 * The rows the order form checks against, from GET /blackout-dates/public.
 *
 * The route answers `{ dates: ['YYYY-MM-DD'], blackoutRules: [{ date, reason, ... }] }`.
 * Each rule is one concrete date (nothing recurring to expand), so prefer
 * `blackoutRules` for the reason shown in the picker, then `dates`. A bare
 * array (or `{ data: [...] }`) is still accepted. `excluded_tags` is ignored
 * here, as the storefront availability check ignores it: any active row
 * blacks the day out. Returns null for anything else.
 */
export const blackoutRowsFrom = (body) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.blackoutRules)) return body.blackoutRules;
  if (Array.isArray(body?.dates)) return body.dates;
  if (Array.isArray(body?.data)) return body.data;
  return null;
};

export const useDeliveryCalendar = () => {
  const [storeHours, setStoreHours] = useState(null);
  const [blackoutDates, setBlackoutDates] = useState([]);
  // Phase 2: zones, cutoff, lead time, days ahead, capacity, pickup — one call.
  // null until loaded or when the request fails (the form then imposes none of them).
  const [rules, setRules] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Loaded when the form opens and again whenever the tab comes back into
    // view, so zones, hours or blackout dates changed in Settings (another
    // tab) reach an order form that stayed open (QA B P2-1: the rules loaded
    // once per page).
    const load = () => {
      const token = (() => {
        try {
          return localStorage.getItem('token');
        } catch {
          return null;
        }
      })();

      // Every request carries the shop's token: without one the backend answers
      // for the default tenant (Twigs), even on the public blackout-dates route.
      fetch(`${API_BASE}${API_PREFIX}/store-settings/hours`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (!cancelled && body?.data?.store_hours) setStoreHours(body.data.store_hours);
        })
        .catch(() => {
          /* fail open — no hours means no days are excluded */
        });

      fetch(`${API_BASE}${API_PREFIX}/delivery/rules`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (!cancelled && body?.data) setRules(body.data);
        })
        .catch(() => {
          /* fail open — no rules means no cutoff, lead time or capacity is imposed */
        });

      fetch(`${API_BASE}${API_PREFIX}/blackout-dates/public`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (cancelled) return;
          const rows = blackoutRowsFrom(body);
          if (rows) setBlackoutDates(rows);
        })
        .catch(() => {
          /* fail open */
        });
    };

    load();
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return { storeHours, blackoutDates, rules };
};

export default useDeliveryCalendar;
